import React, { useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import UrbanPollutionSection from './sections/UrbanPollutionSection';

/*
  SEO REFERENCE (do not change these values):
  ─────────────────────────────────────────────
  Meta Title    : Why Does the Morning Air Feel Different? The Truth About Urban Pollution
  Meta Desc     : Transportation is one of the biggest contributors to urban air pollution.
                  Here's what Chennai's streets look like when a city starts choosing better.
  URL Slug      : /blog/urban-pollution
  Primary KWs   : urban pollution in India, air pollution Chennai, electric auto Chennai,
                  causes of urban pollution, effects of urban pollution,
                  solutions for urban pollution, electric mobility solutions
  Secondary KWs : Chennai, Air Pollution, World Earth Day, Electric Auto, Ola Cab,
                  Electric Vehicle, Sustainability, Carbon Footprint

  Keyword insertion suggestions (NOT applied to body copy per instructions):
  - "urban pollution in India" can be inserted naturally in the intro paragraph
  - "electric auto Chennai" can be added near the NURA section
  - "causes of urban pollution" fits as an H3 under The Problem With Normal
  - "effects of urban pollution" fits as an H3 under Dangerous Comfort
  - "solutions for urban pollution" fits as an H3 under Chennai Is Already Answering
*/

/* ── FAQ Schema — Google Featured Snippets ───────────────── */
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is urban air pollution and why does it matter?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Urban air pollution refers to the contamination of city air by harmful substances like exhaust fumes, dust, and chemical pollutants, primarily from vehicles and industry. It matters because millions of people breathe this air daily, often without realising the long-term damage it causes to their health."
      }
    },
    {
      "@type": "Question",
      "name": "What are the main causes of urban pollution in India?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Vehicular emissions are the single biggest cause of urban pollution in Indian cities. Every petrol auto, cab, and bike on the road releases carbon monoxide, nitrogen oxides, and particulate matter. Construction dust, industrial discharge, and waste burning also contribute significantly."
      }
    },
    {
      "@type": "Question",
      "name": "How do petrol vehicles contribute to urban air pollution?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "It would take nearly 200 trees just to offset the emissions produced by one average petrol vehicle in a year. With millions of petrol vehicles on Indian roads, the cumulative effect on city air quality is enormous, particularly during morning and evening rush hours."
      }
    },
    {
      "@type": "Question",
      "name": "Why do people not react more strongly to urban pollution?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Because exposure is gradual. When pollution increases slowly over years, people adjust their baseline of what feels normal. Polluted air stops feeling alarming and starts feeling familiar, which is precisely what makes it so dangerous."
      }
    },
    {
      "@type": "Question",
      "name": "Can one person's commute choice actually make a difference?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. When millions of individuals each believe their choice does not matter, the collective result is a city that never changes. Switching to an electric auto for your daily commute is one of the most direct, consistent ways to reduce your personal carbon footprint in Chennai."
      }
    },
    {
      "@type": "Question",
      "name": "What is Nura and how does it help reduce air pollution in Chennai?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Nura is an electric mobility company operating in Chennai. Every Nura auto is fully electric, which means zero tailpipe emissions on every trip. With 25,000+ people already using Nura in Chennai, the collective impact on urban air quality is real and measurable."
      }
    }
  ]
};

/* ── Article Schema ──────────────────────────────────────── */
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Why Does the Morning Air Feel Different? The Truth About Urban Pollution",
  "description": "Transportation is one of the biggest contributors to urban air pollution. Here's what Chennai's streets look like when a city starts choosing better.",
  "author": { "@type": "Organization", "name": "Nura eMobility" },
  "publisher": {
    "@type": "Organization",
    "name": "Nura eMobility",
    "url": "https://nura.app"
  },
  "datePublished": "2025-04-22",
  "dateModified": "2025-04-22",
  "url": "https://nura.app/blog/urban-pollution",
  "keywords": "urban pollution in India, causes of urban pollution, effects of urban pollution, solutions for urban pollution, electric mobility solutions, Chennai air pollution, electric auto, carbon footprint, World Earth Day, sustainability"
};

const UrbanPollutionPage = () => {
  useEffect(() => {
    /* ── Meta Title (exact as provided) ── */
    document.title = "Why Does the Morning Air Feel Different? The Truth About Urban Pollution";

    const setMeta = (name, content, attr = 'name') => {
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    };

    /* ── Meta Description (exact as provided) ── */
    setMeta(
      'description',
      "Transportation is one of the biggest contributors to urban air pollution. Here's what Chennai's streets look like when a city starts choosing better."
    );

    /* ── Keywords ── */
    setMeta(
      'keywords',
      'urban pollution in India, causes of urban pollution, effects of urban pollution, solutions for urban pollution, electric mobility solutions, Chennai air pollution, electric auto, Ola Cab, carbon footprint, World Earth Day, electric vehicle, sustainability'
    );

    /* ── Robots ── */
    setMeta('robots', 'index, follow');

    /* ── Canonical URL ── */
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://nura.app/blog/urban-pollution');

    /* ── Open Graph ── */
    setMeta('og:type', 'article', 'property');
    setMeta('og:title', 'Why Does the Morning Air Feel Different? The Truth About Urban Pollution', 'property');
    setMeta('og:description', "Transportation is one of the biggest contributors to urban air pollution. Here's what Chennai's streets look like when a city starts choosing better.", 'property');
    setMeta('og:url', 'https://nura.app/blog/urban-pollution', 'property');
    setMeta('og:site_name', 'Nura eMobility', 'property');

    /* ── Twitter Card ── */
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Why Does the Morning Air Feel Different? The Truth About Urban Pollution');
    setMeta('twitter:description', "Transportation is one of the biggest contributors to urban air pollution. Here's what Chennai's streets look like when a city starts choosing better.");

    /* ── JSON-LD Schemas ── */
    const injectSchema = (id, data) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };
    injectSchema('schema-faq', faqSchema);
    injectSchema('schema-article', articleSchema);

    return () => {
      document.title = 'Nura, Electric Auto Rides';
      ['schema-faq', 'schema-article'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <div className="pt-20">
        <UrbanPollutionSection />
      </div>
      <Footer />
    </div>
  );
};

export default UrbanPollutionPage;
